<template>
<div
    ref="rootRef"
    class="split-pane"
    :class="{ 'split-pane--dragging': dragging, 'split-pane--touch': layoutStore.isTouch }"
>
    <div v-show="!rightOnly" class="split-pane__left" :style="leftStyle">
        <slot name="left" />
    </div>

    <div
        v-show="!rightOnly"
        class="split-pane__handle"
        role="separator"
        aria-orientation="vertical"
        tabindex="0"
        @keydown.left.prevent="nudge(-KEY_STEP)"
        @keydown.right.prevent="nudge(KEY_STEP)"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerEnd"
        @pointercancel="onPointerEnd"
    >
        <span class="split-pane__grip" />
    </div>

    <div v-show="!collapsed || rightOnly" class="split-pane__right">
        <div class="split-pane__right-content" :style="{ minWidth: `${SPLIT_PANE.rightMin}px` }">
            <slot name="right" />
        </div>
    </div>
</div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { SPLIT_PANE } from '@/config/layout'
import { useLayoutStore } from '@/stores/layout'

/**
 * Two panes separated by a draggable bar (GMail style): the left pane keeps a minimum width.
 * While dragging, the right pane is only clipped, its content keeps the `rightMin` width.
 * On release the right pane snaps back to `rightMin`, or on touch devices collapses if less than half of it is visible.
 */
const props = withDefaults(defineProps<{
    defaultRatio?: number
    /** Show only the right pane, as on a phone */
    rightOnly?: boolean
}>(), {
    defaultRatio: 0.5,
    rightOnly: false,
})

const collapsed = defineModel<boolean>('collapsed', { default: false })

const KEY_STEP = 32

const layoutStore = useLayoutStore()
const rootRef = ref<HTMLElement | null>(null)
const ratio = ref(props.defaultRatio)
const dragging = ref(false)
// pointer offset inside the bar when it was grabbed
let grabOffset = 0

const leftStyle = computed(() => collapsed.value
    ? { flex: '1 1 0' }
    : { flex: `0 0 ${ratio.value * 100}%` })

function handleWidth () {
    return rootRef.value?.querySelector<HTMLElement>('.split-pane__handle')?.offsetWidth ?? 0
}

function totalWidth () {
    return rootRef.value?.clientWidth ?? 0
}

/** Moves the bar so the left pane is `leftWidth` px wide; the right pane may end up narrower than `rightMin` */
function moveTo (leftWidth: number) {
    const total = totalWidth()
    if (!total) return

    const clamped = Math.max(SPLIT_PANE.leftMin, Math.min(leftWidth, total - handleWidth()))

    collapsed.value = false
    ratio.value = clamped / total
}

/** Decides what a released bar means: collapse (touch only), or restore the minimum right pane width */
function settle () {
    const total = totalWidth()
    if (!total || collapsed.value) return

    const rightWidth = total - handleWidth() - ratio.value * total

    // a mouse user can always drag the bar back, so the pane only collapses on touch devices
    if (layoutStore.isTouch && rightWidth < SPLIT_PANE.rightMin / 2) {
        collapsed.value = true
    } else if (rightWidth < SPLIT_PANE.rightMin) {
        ratio.value = (total - handleWidth() - SPLIT_PANE.rightMin) / total
    }
}

function currentLeftWidth () {
    const total = totalWidth()
    return collapsed.value ? total - handleWidth() : ratio.value * total
}

function onPointerDown (event: PointerEvent) {
    const handle = event.currentTarget as HTMLElement
    grabOffset = event.clientX - handle.getBoundingClientRect().left
    handle.setPointerCapture(event.pointerId)
    dragging.value = true
}

function onPointerMove (event: PointerEvent) {
    if (!dragging.value || !rootRef.value) return

    moveTo(event.clientX - rootRef.value.getBoundingClientRect().left - grabOffset)
}

function onPointerEnd (event: PointerEvent) {
    if (!dragging.value) return

    dragging.value = false
    ;(event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId)
    settle()
}

function nudge (delta: number) {
    // a collapsed pane reopens at its minimum width
    moveTo(collapsed.value && delta < 0 ? totalWidth() - handleWidth() - SPLIT_PANE.rightMin : currentLeftWidth() + delta)
    settle()
}
</script>

<style scoped>
.split-pane {
    --split-handle-width: 12px;
    --split-line-color: rgba(var(--v-border-color), var(--v-border-opacity));

    display: flex;
    height: 100%;
    min-width: 0;
}

.split-pane--dragging {
    user-select: none;
    cursor: col-resize;
}

.split-pane__left,
.split-pane__right {
    min-width: 0;
    position: relative;
}

.split-pane__left {
    transition: flex-basis 0.15s;
}

.split-pane--dragging .split-pane__left {
    transition: none;
}

.split-pane__right {
    flex: 1 1 0;
    overflow: hidden;
}

/* keeps its width while the clipping container shrinks, so the content is not squeezed */
.split-pane__right-content {
    height: 100%;
    width: 100%;
}

.split-pane__handle {
    position: relative;
    flex: 0 0 var(--split-handle-width);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: col-resize;
    touch-action: none;
    outline: none;
}

/* subtle 1px divider that thickens on hover, as in the GMail web app */
.split-pane__handle::before {
    content: '';
    position: absolute;
    inset: 0 auto 0 50%;
    width: 1px;
    transform: translateX(-50%);
    background-color: var(--split-line-color);
    transition: width 0.15s, background-color 0.15s;
}

.split-pane__handle:hover::before,
.split-pane__handle:focus-visible::before,
.split-pane--dragging .split-pane__handle::before {
    width: 3px;
    background-color: rgb(var(--v-theme-primary));
}

.split-pane__grip {
    display: none;
}

/* wide enough to grab with a finger */
.split-pane--touch {
    --split-handle-width: 36px;
}

.split-pane--touch .split-pane__handle {
    background-color: rgba(var(--v-theme-on-surface), 0.04);
}

.split-pane--touch .split-pane__grip {
    display: block;
    position: relative;
    width: 4px;
    height: 48px;
    border-radius: 2px;
    background-color: rgba(var(--v-theme-on-surface), 0.38);
}

.split-pane--touch .split-pane__handle::before {
    display: none;
}
</style>
